import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Share2, FileBarChart2, Mail, Image } from "lucide-react";

/**
 * Phase 3.36 — Growth Sprint celebration modal.
 *
 * Auto-opens once on first authenticated page load when the user's
 * `growth_welcome_seen` flag is false. Posting the dismiss endpoint
 * flips the flag server-side so the modal never re-opens on refresh.
 *
 * Mounted inside Layout so it surfaces no matter where the user lands
 * after login.
 */
export default function WelcomeGrowthModal() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [daysLeft, setDaysLeft] = useState(null);

  useEffect(() => {
    if (!user) return;
    // Only ask once. Errors fail closed (don't show) so a broken
    // backend never accidentally shows the modal repeatedly.
    api.get("/billing/plans").then(({ data }) => {
      if (data?.growth_welcome_seen === false && data?.current_plan === "growth") {
        setDaysLeft(data?.growth_days_left ?? null);
        setOpen(true);
      }
    }).catch(() => {});
  }, [user]);

  const handleClose = async () => {
    setOpen(false);
    try { await api.post("/billing/dismiss-growth-welcome"); } catch { /* idempotent */ }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="sm:max-w-lg overflow-hidden p-0" data-testid="welcome-growth-modal">
        {/* Hero — slate-900 with emerald accent, matches Array Engine palette */}
        <div className="bg-slate-900 px-7 py-8 text-white relative overflow-hidden">
          <Sparkles className="absolute -right-4 -top-2 w-32 h-32 text-emerald-900" strokeWidth={1} />
          <p className="text-emerald-400 text-xs uppercase tracking-[2px] font-semibold mb-3 relative z-10">
            Congratulations
          </p>
          <DialogHeader className="text-left relative z-10">
            <DialogTitle className="font-heading text-3xl tracking-tight text-white">
              Welcome to Growth.
            </DialogTitle>
            <DialogDescription className="text-slate-300 mt-2 text-sm leading-relaxed">
              {user?.name?.split(" ")[0] || "You"}, your account just leveled up. You now have the keys
              to Array Engine's full reporting engine — for the next {" "}
              <strong className="text-white">{daysLeft ?? 90} days</strong>, on the house.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Unlocked features list */}
        <div className="px-7 py-6">
          <p className="label-overline mb-4">What's unlocked</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-3" data-testid="growth-feature-org-logo">
              <Image className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong className="text-slate-800">Your own org logo</strong> on share links + PDFs (no more Array Engine watermark)</span>
            </li>
            <li className="flex items-start gap-3" data-testid="growth-feature-multi-share">
              <Share2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong className="text-slate-800">Multi-link sharing</strong> — one branded URL per donor, with per-link revoke</span>
            </li>
            <li className="flex items-start gap-3" data-testid="growth-feature-digest">
              <Mail className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong className="text-slate-800">Saturday impact digest</strong> — automatic weekly summary email to your inbox</span>
            </li>
            <li className="flex items-start gap-3" data-testid="growth-feature-analytics">
              <FileBarChart2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <span><strong className="text-slate-800">Personal & team analytics</strong> — time-on-task, contributions, member activity</span>
            </li>
          </ul>
        </div>

        <DialogFooter className="px-7 pb-7 pt-0">
          <Button
            onClick={handleClose}
            className="bg-emerald-600 hover:bg-emerald-700 text-white w-full sm:w-auto"
            data-testid="welcome-growth-dismiss-button"
          >
            Let's go →
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
